import { useMemo, useState, useEffect, useCallback } from 'react';
import {
  List,
  Button,
  Badge,
  Card,
  Checkbox,
  Empty,
  Popconfirm,
  Result,
  Space,
  Tag,
  Typography,
  message,
  Modal,
  Input,
  Select,
  Progress,
  Upload,
} from 'antd';
import {
  CloudUploadOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  ExportOutlined,
  UploadOutlined,
  PlayCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { submitPapers, loadCart, previewPaperMetadata, confirmPaperMetadata, getPaperLibrary, downloadLibraryText } from '../services/api';
import PaperMetadataModal from '../components/PaperMetadataModal';
import { paperReferenceName } from '../utils/paperReferenceName';
import { removeFromCart, removeManyFromCart } from '../utils/cart';

import { MAX_FILES, MAX_FILE_SIZE, MAX_FILE_SIZE_MB } from '../config';

const { Title, Paragraph, Text } = Typography;

export default function PapersPage({ user }) {
  const [papers, setPapers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [error, setError] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [titleModalOpen, setTitleModalOpen] = useState(false);
  const [graphTitle, setGraphTitle] = useState('');
  const [extractionVersion, setExtractionVersion] = useState('v1');
  const [generationModel, setGenerationModel] = useState('gpt-6-astra');
  const [chatModel, setChatModel] = useState('gpt-6-astra');
  const [publicAccess, setPublicAccess] = useState(false);
  const [metadataPaper, setMetadataPaper] = useState(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [library, setLibrary] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);

  async function identifyPdf(paper) {
    if (!/\.pdf$/i.test(paper.pdfFile?.name || '')) return;
    setPapers((current) => current.map((row) => row.id === paper.id ? { ...row, identifying: true } : row));
    try {
      const preview = await previewPaperMetadata({ file: paper.pdfFile });
      setPapers((current) => current.map((row) => row.id === paper.id && row.pdfFile === paper.pdfFile
        ? { ...row, identifying: false, metadataPreview: preview } : row));
    } catch {
      setPapers((current) => current.map((row) => row.id === paper.id ? { ...row, identifying: false } : row));
    }
  }

  async function confirmMetadata(result) {
    try {
      const confirmed = await confirmPaperMetadata(result.token);
      const updated = { ...metadataPaper, title: paperReferenceName(confirmed.metadata, result.metadata.title), metadata: confirmed.metadata,
        metadata_token: result.token, metadataPreview: result, isCustom: true, selected: true };
      setPapers((current) => current.some((row) => row.id === updated.id)
        ? current.map((row) => row.id === updated.id ? updated : row) : [...current, updated]);
      setMetadataPaper(null);
      message.success('Metadata confirmed and saved.');
    } catch (error) { message.error(error.message); }
  }

  async function openLibrary() {
    setLibraryOpen(true); setLibraryLoading(true);
    try { setLibrary((await getPaperLibrary()).map((asset) => ({ ...asset, name: paperReferenceName(asset.metadata, asset.name) }))); }
    catch (error) { message.error(error.message); }
    finally { setLibraryLoading(false); }
  }

  useEffect(()=> {
    console.log('upload progress updated:', uploadProgress)
  }, [uploadProgress])

  const selectedPapers = useMemo(
    () => papers?.filter((paper) => paper.selected),
    [papers]
  );

  useEffect(() => {
    getCart();
    // Cross-tab sync: if the /add/:id popup (or another tab) mutates the
    // cart in localStorage, re-fetch and merge so we don't drop per-row UI
    // state (selected, pdfFile) on rows we already have.
    function handleStorage(e) {
      if (e.key && e.key !== 'datavault_kg_cart') return;
      loadCart().then((pubs) => {
        setPapers((current) => {
          const byId = new Map(current.map((p) => [p.id, p]));
          return pubs.map((pub) => {
            const existing = byId.get(pub.id);
            return existing ? { ...pub, selected: existing.selected, pdfFile: existing.pdfFile } : pub;
          });
        });
      }).catch(() => {});
    }
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  async function getCart() {
    setLoading(true);
    setError(false);
    try {
      const pubs = await loadCart();
      setPapers(pubs);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  function toggleSelected(id) {
    setPapers((current) =>
      current.map((paper) =>
        paper.id === id ? { ...paper, selected: !paper.selected } : paper
      )
    );
  }

  function toggleSelectAll(shouldSelect) {
    setPapers((current) =>
      current.map((paper) => ({ ...paper, selected: shouldSelect }))
    );
  }

  function deletePaper(id) {
    removeFromCart(id);
    setPapers((current) => current.filter((paper) => paper.id !== id));
  }

  function deleteSelectedPapers() {
    const selectedIds = selectedPapers.map((paper) => paper.id);
    if (selectedIds.length === 0) return;
    removeManyFromCart(selectedIds);
    const selectedIdSet = new Set(selectedIds);
    setPapers((current) => current.filter((paper) => !selectedIdSet.has(paper.id)));
    message.success(`${selectedIds.length} paper${selectedIds.length === 1 ? '' : 's'} removed.`);
  }

  function renamePaper(id, nextTitle) {
    const title = nextTitle.trim();
    if (!title) {
      message.warning('Paper name cannot be empty.');
      return;
    }
    setPapers((current) => current.map((paper) => (
      paper.id === id ? { ...paper, title } : paper
    )));
  }

  function addPdf(paper, file) {
    if (!file) {
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      message.warning(`File "${paper.title}" exceeds ${MAX_FILE_SIZE_MB}MB limit`);
      return;
    }

    setPapers((current) =>
      current.map((currentPaper) =>
        currentPaper.id === paper.id
          ? { ...currentPaper, pdfFile: file, metadata: null, metadata_token: null, metadataPreview: null }
          : currentPaper
      )
    );
    identifyPdf({ ...paper, pdfFile: file });
  }

  function createCustomPaper(files) {
    const candidates = Array.from(files || []);
    const supportedFiles = candidates.filter((file) => {
      const supported = (
        file.type === 'application/pdf'
        || file.type === 'text/plain'
        || /\.(pdf|txt)$/i.test(file.name)
      );
      if (!supported) message.warning(`File "${file.name}" must be a PDF or TXT file.`);
      return supported;
    });
    const eligibleFiles = supportedFiles.filter((file) => {
      const allowed = file.size <= MAX_FILE_SIZE;
      if (!allowed) {
        message.warning(`File "${file.name}" exceeds ${MAX_FILE_SIZE_MB}MB limit.`);
      }
      return allowed;
    });
    const availableSlots = Math.max(0, MAX_FILES - papers.length);
    if (availableSlots === 0) {
      message.warning(`Maximum ${MAX_FILES} papers allowed per graph.`);
      return;
    }
    if (eligibleFiles.length > availableSlots) {
      message.warning(
        `Only ${availableSlots} more paper${availableSlots === 1 ? '' : 's'} can be added.`
      );
    }

    const convertedPapers = eligibleFiles.slice(0, availableSlots).map((file, index) => {
      return {
        id: `${Date.now()}-${index}-${Math.random().toString(36).slice(2)}`,
        title: file.name,
        publisher: '',
        pdfFile: file,
        has_pdf: false,
        isCustom: true,
        selected: true,
      }
    });
    if (convertedPapers.length) {
      setPapers((current) => [...current, ...convertedPapers]);
      convertedPapers.forEach(identifyPdf);
      message.success(
        `${convertedPapers.length} custom paper${convertedPapers.length === 1 ? '' : 's'} added.`
      );
    }
  }

  useEffect(()=>{
    console.log('paper selection changed:', selectedPapers);
  }, [selectedPapers])

  const submitSelectedPapers = useCallback(async () => {
    const trimmedTitle = graphTitle.trim();
    if (!trimmedTitle) {
      message.warning('Enter a title for the knowledge graph.');
      return;
    }
    console.log('submitting papers', selectedPapers)
    if (selectedPapers.length === 0) {
      message.warning('Select at least one paper before starting generation.');
      return;
    }

    if (papers.length > MAX_FILES) {
      message.warning(`Maximum ${MAX_FILES} papers allowed per graph`);
      return;
    }

    if (extractionVersion !== 'v1' && selectedPapers.length>15) {
      message.warning('v2.2 supports at most 15 PDFs.'); return;
    }
    if (extractionVersion !== 'v1' && selectedPapers.some(paper => paper.pdfFile && !/\.pdf$/i.test(paper.pdfFile.name))) {
      message.warning('v2.2 accepts PDF uploads only.'); return;
    }
    if (selectedPapers.some((paper) => paper.identifying)) {
      message.info('Article identification is still running. Please wait.');
      return;
    }
    const needsConfirmation = selectedPapers.find((paper) => paper.metadataPreview?.token && !paper.metadata_token);
    if (needsConfirmation) {
      setTitleModalOpen(false);
      setMetadataPaper(needsConfirmation);
      return;
    }
    const paperMissingPdf = selectedPapers.find(
      (paper) => !paper.has_pdf && !paper.pdfFile && !paper.asset_id
    );

    if (paperMissingPdf) {
      Modal.warning({
        title:'Missing PDF',
        content: `Paper ${paperMissingPdf.title} is missing a pdf in the current system; Please provide pdf downloaded from publisher.`,
        okText: 'Got it',
      });
      return;
    }

    try {
      setSubmitting(true);
      await submitPapers(
        selectedPapers,
        setUploadProgress,
        trimmedTitle,
        generationModel,
        chatModel,
        user?.authenticated ? publicAccess : true,
        extractionVersion
      );
      setTitleModalOpen(false);
      setGraphTitle('');
      message.success('Papers submitted! Start KG generation in Jobs tab.');
      navigate('/index');
    } catch (error) {
      if (error?.message?.toLowerCase().includes('graph limit')) {
        Modal.warning({
          title: 'Graph limit reached',
          content:
            'You have reached the maximum number of graphs for this session. Delete an existing graph in Manage Graphs before generating a new one.',
          okText: 'Got it',
        });
      } else if (error.message) {
        Modal.warning({
          title: 'Error submitting papers',
          content: error.message,
          okText: 'Got it',
        });
      } else {
        message.error('Failed to submit papers.');
      }
      setUploadProgress(null);
    } finally {
      setSubmitting(false);
    }
  }, [selectedPapers, papers, graphTitle, generationModel, chatModel, publicAccess, extractionVersion, user, navigate])

  const totalCount = papers.length;
  const selectedCount = selectedPapers?.length ?? 0;
  const allSelected = totalCount > 0 && selectedCount === totalCount;
  const someSelected = selectedCount > 0 && selectedCount < totalCount;

  const renderItem = (record) => (
    <List.Item
      style={{
        alignItems: 'flex-start',
        padding: '16px 12px',
        background: record.selected ? 'rgba(99, 102, 241, 0.04)' : 'transparent',
        borderRadius: 6,
        transition: 'background 0.15s ease',
      }}
      actions={[
          <Popconfirm
            title="Delete this paper?"
            description="This only removes it from your cart."
            okText="Delete"
            cancelText="Cancel"
            onConfirm={() => deletePaper(record.id)}
          >
            <Button danger icon={<DeleteOutlined />} size="small">
              Delete
            </Button>
          </Popconfirm>
        ]}
    >
      <Space align="start" size={12} style={{ marginRight: 16 }}>
          <Checkbox
            checked={record.selected}
            onChange={() => toggleSelected(record.id)}
            style={{ marginTop: 4 }}
            aria-label={`Select ${record.title}`}
          />
      </Space>
      <List.Item.Meta 
        title={
          <Text
            editable={record.isCustom ? {
              onChange: (nextTitle) => renamePaper(record.id, nextTitle),
              maxLength: 255,
              tooltip: 'Rename uploaded paper',
            } : false}
            style={{ fontSize: 14, fontWeight: 600 }}
          >
            {record.title}
          </Text>
        }
        description={
          <Space orientation="vertical" size={6} style={{ width: '100%' }}>
            {record.abstract && (
              <Paragraph 
                style={{ fontSize: 12, color: '#888' }}
                ellipsis={{rows: 4, expandable: true}}>
                {record.abstract}
              </Paragraph>
            )}
            {record.metadata?.citation && <Paragraph copyable>{record.metadata.citation}</Paragraph>}
            {record.isCustom && !record.asset_id && <Button size="small" loading={record.identifying}
              icon={!record.metadata_token ? <WarningOutlined style={{ color: '#d48806' }} title="Metadata needs confirmation" /> : undefined}
              onClick={() => setMetadataPaper(record)}>
              {record.metadata_token ? 'Review metadata' : 'Identify / confirm metadata'}
            </Button>}
            {!record.has_pdf && !record.asset_id && (
                <Space size={8}>
                  {record.pdfFile ? (
                    <Tag color="cyan" bordered={false}>
                      {record.pdfFile.name}
                    </Tag>
                  ) : (
                    <>
                      <Space size={4}>
                        <Tag color="red" bordered={true} style={{ fontSize: 11 }}>
                          Missing PDF
                        </Tag>
                        <a
                          href={record.publisher}
                          target="_blank"
                          rel="noreferrer"
                          style={{ fontSize: 11 }}
                        >
                          Download from publisher
                        </a>
                      </Space>
                      <input
                        id={`pdf-upload-${record.id}`}
                        type="file"
                        accept="application/pdf"
                        style={{ display: 'none' }}
                        onChange={(event) => {
                          addPdf(record, event.target.files?.[0]);
                          event.target.value = '';
                        }}
                      />
                      <Button
                        size="small"
                        icon={<UploadOutlined />}
                        onClick={() =>
                          document.getElementById(`pdf-upload-${record.id}`)?.click()
                        }
                      >
                        Upload PDF
                      </Button>
                      </>
                    )}
                </Space>
            )}
          </Space>
        }
      />
    </List.Item>
  )

  if (error) {
    return (
      <Result
        status="error"
        title="Unable to load papers"
        subTitle="There was a problem loading your selected papers. Please try again."
        extra={[
          <Button key="retry" type="primary" loading={loading} onClick={getCart}>
            Retry
          </Button>,
        ]}
      />
    );
  }

  return (
    <>
      <PaperMetadataModal paper={metadataPaper} onCancel={() => setMetadataPaper(null)} onConfirm={confirmMetadata} />
      <Modal title="Saved papers" open={libraryOpen} onCancel={() => setLibraryOpen(false)} footer={null}>
        <List loading={libraryLoading} dataSource={library} locale={{ emptyText: 'No saved papers yet. Confirm a citation or upload a PDF to save it.' }} renderItem={(asset) => (
          <List.Item>
            <Space orientation="vertical" style={{ width: '100%' }}>
              <Text strong>{asset.name}</Text>
              {asset.metadata?.citation && <Paragraph copyable>{asset.metadata.citation}</Paragraph>}
              <Text type="secondary">{asset.clean_text_ready ? 'Clean TXT ready' : 'Citation saved; clean TXT not available yet'}</Text>
              <Space>
                <Button disabled={!asset.clean_text_ready} onClick={() => downloadLibraryText(asset.id).catch((error) => message.error(error.message))}>Download TXT</Button>
                <Button disabled={!asset.clean_text_ready} onClick={() => {
                  setPapers((current) => current.some((row) => row.asset_id === asset.id) ? current : [...current, {
                    id: `asset-${asset.id}`, asset_id: asset.id, title: asset.name, metadata: asset.metadata,
                    has_pdf: false, isCustom: true, selected: true,
                  }]);
                  setLibraryOpen(false);
                }}>Use in graph</Button>
              </Space>
            </Space>
          </List.Item>
        )} />
      </Modal>
      <Modal
        title="Name your knowledge graph"
        open={titleModalOpen}
        okText="Create graph"
        cancelText="Cancel"
        confirmLoading={submitting}
        okButtonProps={{ disabled: !graphTitle.trim() }}
        onOk={submitSelectedPapers}
        onCancel={() => {
          if (!submitting) setTitleModalOpen(false);
        }}
        destroyOnHidden
      >
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          <Input
            autoFocus
            value={graphTitle}
            maxLength={255}
            showCount
            placeholder="Enter graph title"
            onChange={(event) => setGraphTitle(event.target.value)}
            onPressEnter={() => {
              if (graphTitle.trim() && !submitting) submitSelectedPapers();
            }}
          />
          <div>
            <Text strong>Graph generation mode</Text>
            <Select
              value={extractionVersion}
              onChange={setExtractionVersion}
              style={{ width: '100%', marginTop: 8 }}
              options={[
                { value: 'v1', label: 'v1 — Standard extraction' },
                { value: 'v2', label: 'v2.2 — Full graph with statistical preview' },
                { value: 'v2_stats', label: 'v2.2 — Statistical relations only' },
              ]}
            />
            <Text type="secondary">{extractionVersion === 'v2_stats'
              ? 'Generate a graph of reported statistical results and their variables, including non-significant results, with source evidence and page references.'
              : 'v2.2 accepts PDFs. A statistical graph and Excel become available after Stage 1 while full graph generation continues.'}</Text>
          </div>
          <div>
            <Text strong>Graph generation model</Text>
            <Select
              value={generationModel}
              onChange={setGenerationModel}
              style={{ width: '100%', marginTop: 8 }}
              options={[
                { value: 'gpt-6-astra', label: 'GPT-6 Astra (OpenAI)' },
                { value: 'gpt-6.1-sol', label: 'GPT-6.1 Sol (OpenAI)' },
                { value: 'claude-opus-5-5', label: 'Claude Opus 5.5' },
                { value: 'grok-4.6', label: 'Grok 4.6' },
                { value: 'gemini-3.8-flash', label: 'Gemini 3.8 Flash' },
                { value: 'mistral-medium-3-5', label: 'Mistral Medium 3.5' },
                { value: 'DeepSeek-V4.1-Flash', label: 'DeepSeek V4.1 Flash' },
                { value: 'qwen--qwen38-27b', label: 'Qwen 3.8 27B' },
                { value: 'kimi-k3', label: 'Kimi K3' },
              ]}
            />
          </div>
          <div>
            <Text strong>Graph chat model</Text>
            <Select
              value={chatModel}
              onChange={setChatModel}
              style={{ width: '100%', marginTop: 8 }}
              options={[
                { value: 'gpt-6-astra', label: 'GPT-6 Astra (OpenAI)' },
                { value: 'gpt-6.1-sol', label: 'GPT-6.1 Sol (OpenAI)' },
                { value: 'claude-opus-5-5', label: 'Claude Opus 5.5' },
                { value: 'grok-4.6', label: 'Grok 4.6' },
                { value: 'gemini-3.8-flash', label: 'Gemini 3.8 Flash' },
                { value: 'mistral-medium-3-5', label: 'Mistral Medium 3.5' },
                { value: 'DeepSeek-V4.1-Flash', label: 'DeepSeek V4.1 Flash' },
                { value: 'qwen--qwen38-27b', label: 'Qwen 3.8 27B' },
                { value: 'kimi-k3', label: 'Kimi K3' },
              ]}
            />
          </div>
          <Text type="secondary">
            Each model uses its highest supported reasoning level.
          </Text>
          {user?.authenticated ? (
            <Checkbox
              checked={publicAccess}
              onChange={(event) => setPublicAccess(event.target.checked)}
            >
              Public access — anyone with the link can view and query this graph
            </Checkbox>
          ) : (
            <Text type="secondary">
              Public graphs can be viewed and queried by anyone with the link.
            </Text>
          )}
        </Space>
      </Modal>
      <Space orientation="vertical" size={16} style={{ width: '100%', paddingBottom: 96 }}>
        <div>
          <Title level={4} style={{ marginBottom: 4 }}>
            Choose your papers
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Choose the papers to include in your knowledge graph. Papers from GUSTO Data Vault
            and custom uploads share a combined maximum of {MAX_FILES} papers.
          </Paragraph>
        </div>

        <div className="paper-source-grid">
          <Card className="paper-source-card" bordered={false}>
            <Space orientation="vertical" size={16} style={{ width: '100%' }}>
              <DatabaseOutlined className="paper-source-card__icon" />
              <div>
                <Title level={4}>Select from GUSTO Data Vault</Title>
                <Paragraph type="secondary">
                  Browse publications and add papers to this workspace from the Data Vault.
                </Paragraph>
              </div>
              <Button
                type="primary"
                icon={<ExportOutlined />}
                href="https://gustodatavault-staging.com/pubs"
                target="_blank"
                rel="noopener noreferrer"
              >
                Open GUSTO Data Vault
              </Button>
            </Space>
          </Card>

          <Card className="paper-source-card" bordered={false}>
            <Space orientation="vertical" size={16} style={{ width: '100%' }}>
              <CloudUploadOutlined className="paper-source-card__icon" />
              <div>
                <Title level={4}>Upload custom papers</Title>
                <Paragraph type="secondary">
                  Add PDF or plain-text papers directly from your computer.
                </Paragraph>
              </div>
              <Upload.Dragger
                className="paper-source-dragger"
                accept=".pdf,.txt,application/pdf,text/plain"
                multiple
                showUploadList={false}
                beforeUpload={(file, fileList) => {
                  if (file.uid === fileList[0]?.uid) createCustomPaper(fileList);
                  return Upload.LIST_IGNORE;
                }}
              >
                <p className="ant-upload-drag-icon">
                  <CloudUploadOutlined />
                </p>
                <p className="ant-upload-text">Drag papers here or click to browse</p>
                <p className="ant-upload-hint">PDF or TXT</p>
              </Upload.Dragger>
            </Space>
          </Card>
        </div>

        <div>
          <Title level={4} style={{ marginBottom: 4 }}>
            Selected Papers
          </Title>
          <Space style={{ marginBottom: 16 }}>
            <Button onClick={openLibrary}>Saved papers</Button>
            <Button onClick={() => setMetadataPaper({ id: `citation-${Date.now()}`, isCustom: true, has_pdf: false })}>Add by DOI / PMID</Button>
          </Space>
        </div>

        {papers.length === 0 ? (
          <Empty
            style={{ padding: '48px 0' }}
            description={
              <Typography.Text>
                No papers added. Get started by adding papers from{' '}
                GUSTO Data Vault or uploading custom papers above.
              </Typography.Text>
            }
          />
        ) : (
          <div
            style={{
              border: '1px solid var(--app-line, #f0f0f0)',
              borderRadius: 8,
              background: '#fff',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                borderBottom: '1px solid #f0f0f0',
                background: '#fafafa',
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
              }}
            >
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={(e) => toggleSelectAll(e.target.checked)}
              >
                <Text strong>
                  {allSelected ? 'Deselect all' : 'Select all'}
                </Text>
              </Checkbox>
              <Space>
                <Text type="secondary">
                  {selectedCount} of {totalCount} selected
                </Text>
                <Popconfirm
                  title={`Delete ${selectedCount} selected paper${selectedCount === 1 ? '' : 's'}?`}
                  description="This only removes them from your paper list."
                  okText="Delete"
                  cancelText="Cancel"
                  disabled={selectedCount === 0}
                  onConfirm={deleteSelectedPapers}
                >
                  <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    disabled={selectedCount === 0}
                  >
                    Delete selected
                  </Button>
                </Popconfirm>
              </Space>
            </div>
            <List
              itemLayout="horizontal"
              dataSource={papers}
              split={true}
              rowKey="id"
              renderItem={renderItem}
              style={{ padding: '0 8px' }}
            />
          </div>
        )}
      </Space>

      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '12px 24px',
          background: '#fff',
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          zIndex: 100,
          boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.04)',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          {uploadProgress && (
            <Progress
              percent={Math.round((uploadProgress.current / uploadProgress.total) * 100)}
              status={
                uploadProgress.current === uploadProgress.total ? 'success' : 'active'
              }
            />
          )}
        </div>

        <Badge
          count={selectedCount}
          showZero
          overflowCount={999}
          color={selectedCount > 0 ? '#6366f1' : '#bfbfbf'}
          offset={[-4, 4]}
        >
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            loading={submitting}
            disabled={selectedCount === 0}
            onClick={() => setTitleModalOpen(true)}
          >
            Generate Knowledge Graph
          </Button>
        </Badge>
      </div>
    </>
  );
}
